import FormGroup from "features/shared/forms/components/FormGroup";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import reactSelectStyles from "features/shared/components/reactSelect/reactselectcustom.module.scss";
import { UserEnumDTO } from "features/shared/enums/dynamic/UserEnumDTO";
import RadioBox from "features/shared/forms/components/radioBox/RadioBox";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import PageCard from "features/shared/components/card/PageCard";
import { ContractingProcessAnswerProps } from "features/contracting/models/ContractingProcessAnswerProps";
import {
  setContractEffectiveDateValidation,
  setYesTerminationByUsValidations,
} from "features/contracting/sections/contractingForms/processForm/processFormValidation";
import DatePicker from "features/shared/components/reactDates/DatePicker";
import SupplierSelect from "features/contracting/components/SupplierSelect";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import ContractingState from "infrastructure/store/contracting/ContractingState";
import { useContractingCustomerTypeValue } from "features/contracting/hooks/useContractingCustomerTypeValue";
import { Commodity } from "features/shared/enums/commodity";
import { TerminationSchemeEnumDTO } from "features/shared/enums/dynamic/TerminationSchemeEnumDTO";
import { isFixedTermProlongation } from "features/contracting/sections/contractingForms/processForm/processFormHelper";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

interface Props extends ContractingProcessAnswerProps {}

const YesTerminationByUsSubForm = ({ formHandler }: Props) => {
  // translations
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "yesTerminationByUsSubForm");

  // store
  const configuration = useSelector<IAppState, ContractingState>(
    (store) => store.contracting
  ).configuration;

  const commodity = useContractingCustomerTypeValue<Commodity>("commodity");
  const suppliers: UserEnumDTO[] = configuration.suppliers!.filter(
    (x) => !x.isParentCompany && x.commodity === commodity
  );

  const terminationSchemes: TerminationSchemeEnumDTO[] =
    configuration.terminationSchemes!;

  const contractDurationTypes: UserEnumDTO[] =
    configuration.contractDurationTypes!.filter((x) => x.code !== "FixedTerm");

  // form hook props
  const {
    bindReactSelectField,
    bindCheckField,
    bindDatePickerField,
    clearFormValues,
    setValidationRule,
    errors,
    isFilledFormValues,
    getFormValue,
  } = { ...formHandler };

  // helpers
  const showEffectiveDate = () => {
    return isFixedTermProlongation(getContractDuration());
  };

  const getContractDuration = () => {
    return getFormValue<string>("yesTerminationByUs.contractDurationTypeCode");
  };

  const clearEffectiveDate = () => {
    setContractEffectiveDateValidation(setValidationRule, "remove");
    clearFormValues(["yesTerminationByUs.contractEffectiveDate"]);
  };

  const getTerminationSchemes = () => {
    return terminationSchemes.filter(
      (x) => x.contractDurationType === getContractDuration()
    );
  };

  const clearTerminationScheme = () => {
    const selectedScheme = getFormValue(
      "yesTerminationByUs.terminationSchemeCode"
    );
    const schemesByDuration = getTerminationSchemes();
    // cleare only if doesn't exists in schemes
    if (!schemesByDuration.some((x) => x.code === selectedScheme)) {
      clearFormValues(["yesTerminationByUs.terminationSchemeCode"]);
    }
  };

  // side-effects
  useEffect(() => {
    // set default validations
    setYesTerminationByUsValidations(setValidationRule);

    return () => {
      // before unmount component remove values and validations
      setYesTerminationByUsValidations(setValidationRule, "remove");
      clearFormValues(["yesTerminationByUs"]);
    };
  }, []);

  useEffect(() => {
    if (showEffectiveDate()) {
      setContractEffectiveDateValidation(setValidationRule);
    } else {
      clearEffectiveDate();
    }
    clearTerminationScheme();

    return () => {
      clearEffectiveDate();
    };
  }, [getContractDuration()]);

  // sub-renders
  const renderContractDurationTypes = () => {
    // infinity
    return contractDurationTypes.map((type, index) => (
      <div className="col-md-6 px-2" key={index}>
        <RadioBox
          label={tLabels(
            `contractDurationType.${type.code}`
          )}
          icon={{ icon: "clock" }}
          {...bindCheckField(
            "yesTerminationByUs.contractDurationTypeCode",
            type.code
          )}
          heightAsFormControl
          className="mb-md-0"
        />
      </div>
    ));
  };

  // render
  return (
    <div>
      <PageCard
        title={tLabels("currentSupplierCardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <SupplierSelect
          name="yesTerminationByUs.currentSupplierCode"
          suppliers={suppliers}
          bindReactSelectField={bindReactSelectField}
          errors={errors}
        />
      </PageCard>
      <PageCard
        title={tLabels("contractDurationCardTitle")}
        bodyClassName="pt-4 px-4"
        cardClassName="mb-4"
      >
        <div className="row mx-n2">
          <div className="col-12 px-2">
            <FormGroup
              labelText={tLabels("contractDurationTypeCode.label")}
              validationError={
                errors["yesTerminationByUs.contractDurationTypeCode"]
              }
            >
              <div className="row mx-n2">{renderContractDurationTypes()}</div>
            </FormGroup>
          </div>
          <RenderWrapper
            isVisible={() =>
              isFilledFormValues([
                "yesTerminationByUs.contractDurationTypeCode",
              ])
            }
          >
            <div className="col-md-6 px-2">
              <FormGroup
                labelText={tLabels("terminationSchemeCode.label")}
                validationError={
                  errors["yesTerminationByUs.terminationSchemeCode"]
                }
              >
                <Select
                  {...bindReactSelectField<UserEnumDTO>(
                    "yesTerminationByUs.terminationSchemeCode",
                    "code",
                    getTerminationSchemes()
                  )}
                  getOptionLabel={(scheme) => scheme.name!}
                  captureMenuScroll={false}
                  className={reactSelectStyles.customSelect}
                  classNamePrefix="customSelect"
                  placeholder={tLabels("terminationSchemeCode.placeholder")}
                  noOptionsMessage={() => tLabels("terminationSchemeCode.noOptionsMessage")}
                  isSearchable={false}
                />
              </FormGroup>
            </div>
            <div className="col-md-6 px-2">
              <FormGroup
                labelText={tLabels("contractEffectiveDate.label")}
                inputId="yesTerminationByUs.contractEffectiveDate"
                validationError={
                  errors["yesTerminationByUs.contractEffectiveDate"]
                }
                visible={showEffectiveDate()}
                tooltip={{
                  id: "contractEffectiveDate-tooltip",
                  content:
                    tLabels("contractEffectiveDate.tooltip"),
                  placement: "top",
                }}
              >
                <DatePicker
                  {...bindDatePickerField(
                    "yesTerminationByUs.contractEffectiveDate"
                  )}
                  onlySelectable
                  minDate={new Date()}
                />
              </FormGroup>
            </div>
          </RenderWrapper>
        </div>
      </PageCard>
    </div>
  );
};

export default YesTerminationByUsSubForm;
